import React, { useState } from 'react'
import { ConfirmDialog } from './confirm-dialog'
import { ErrorReference } from '../error/error-reference'

const people = n => (n === 1 ? '1 person' : `${n} people`)

// What a connection's status means, in a sentence under the switch (owner
// 2026-10-23): it changes with the choice, and names testing while the test
// list has addresses (testers: its length), as the header pill does.
export const statusSentence = ({ active, testers }) => {
  const testing = typeof testers === 'number' && testers > 0
  if (active) {
    return testing
      ? `People at its domains are sent here to sign in. Testing: only the ${people(testers)} on its test list ${testers === 1 ? 'is' : 'are'} sent here until the list is cleared.`
      : 'People at its domains are sent here to sign in.'
  }
  return testing
    ? `Nobody can sign in with it; its domains fall back to their other connections. Its test list (${people(testers)}) is kept for when it's switched on.`
    : 'Nobody can sign in with it; its domains fall back to their other connections.'
}

// A connection's Status as an on/off control: Active / Inactive, the
// consequence under it, and -- for a connection that serves sign-ins now
// (servingNow) -- the page's own confirmation before Inactive, naming what
// happens and how to undo it (undo). The staff workspace binds it to the
// form (saved with Save); the organization's connection page saves at once,
// so onChange may return a promise: the dialog waits for it and shows a
// refusal (an Error's message, its `failure` the reference).
export const StatusSwitch = ({ id = 'active', label = 'Status', active, onChange, disabled, testers, connectionName, domains, servingNow, undo, after }) => {
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const name = connectionName || 'this connection'

  const apply = async next => {
    setBusy(true)
    setError(null)
    try {
      await onChange(next)
      setAsking(false)
    } catch (err) {
      setError(err || new Error("The status couldn't be changed."))
    } finally {
      setBusy(false)
    }
  }

  const choose = next => {
    if (disabled || busy || next === Boolean(active)) return
    if (!next && servingNow) { setError(null); setAsking(true); return }
    apply(next)
  }

  const segment = (value, text) =>
    <button type="button" role="radio" aria-checked={Boolean(active) === value} className={`sw-segment ${Boolean(active) === value ? 'sw-on' : ''}`.trim()}
      disabled={disabled || busy} onClick={() => choose(value)}>{text}</button>

  return (
    <div className="sw-field" data-testid="status-switch">
      <span className="sw-label d-block" id={`${id}-label`}>{label}</span>
      <div className="sw-segmented" role="radiogroup" aria-labelledby={`${id}-label`} aria-describedby={`${id}-sentence`} id={id}>
        {segment(true, 'Active')}
        {segment(false, 'Inactive')}
      </div>
      <div className="sw-hint" id={`${id}-sentence`} data-testid="status-sentence">{statusSentence({ active, testers })}</div>
      {after}
      {error && !asking && <p className="text-danger small m-0" role="alert">{error.message}</p>}
      {error && !asking && <ErrorReference error={error.failure || error} className="mt-0" />}
      <ConfirmDialog
        open={asking}
        title={`Switch off ${name}?`}
        body={`Nobody can sign in with ${name}; people at ${domains || 'its domains'} fall back to their other connections, or a password ("Forgot password?" on the sign-in page sets one). ${undo || 'To undo, choose Active again.'}`}
        confirmText="Switch off"
        busy={busy}
        error={asking && error ? error.message : null}
        failure={asking && error ? (error.failure || error) : null}
        onConfirm={() => apply(false)}
        onCancel={() => { setAsking(false); setError(null) }}
      />
    </div>
  )
}
